import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import type { WhyGuestPillar } from '@/content/whyGuestsPillars'

const mediaBaseClass =
  'absolute inset-0 h-full w-full object-cover transition-[transform] duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100'

export function WhyGuestPillarCard({ pillar }: { pillar: WhyGuestPillar }) {
  const reduced = useReducedMotion()
  const titleId = `why-guest-${pillar.id}-title`
  const useVideo = Boolean(pillar.videoSrc && !reduced)

  return (
    <article
      aria-labelledby={titleId}
      className="group relative flex h-full min-h-[26rem] flex-col overflow-hidden rounded-[1.85rem] shadow-[var(--shadow-card)] ring-1 ring-black/10 transition-[transform,box-shadow] duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1 hover:shadow-[var(--shadow-card-hover)] motion-reduce:transform-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-[var(--shadow-card)] sm:min-h-[28rem] lg:min-h-[30rem]"
    >
      {/* Full-card photography or muted loop video */}
      {useVideo && pillar.videoSrc ? (
        <video
          className={cn(mediaBaseClass)}
          src={pillar.videoSrc}
          poster={pillar.imageSrc}
          muted
          playsInline
          loop
          autoPlay
          preload="metadata"
          aria-hidden
        />
      ) : (
        <img
          src={pillar.imageSrc}
          alt=""
          width={960}
          height={1200}
          loading="lazy"
          decoding="async"
          className={cn(mediaBaseClass)}
        />
      )}
      <span className="sr-only">{pillar.imageAlt}</span>

      {/* Light brand wash + top vignette so the frame still feels “Rodeo” */}
      <div
        className={cn(
          'pointer-events-none absolute inset-0 bg-gradient-to-b opacity-[0.18]',
          pillar.headerGradient
        )}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-transparent"
        aria-hidden
      />

      {/* Bottom scrim for readable overlay copy */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[62%] bg-gradient-to-t from-black/92 via-black/55 to-transparent sm:h-[58%]"
        aria-hidden
      />

      {/* Copy — bottom-aligned, high contrast */}
      <div className="relative z-10 mt-auto flex flex-col items-center px-6 pb-8 pt-24 text-center sm:px-8 sm:pb-9 sm:pt-28">
        <h3
          id={titleId}
          className="max-w-[20rem] font-display text-xl font-semibold uppercase leading-snug tracking-[0.12em] text-white [text-shadow:0_2px_24px_rgba(0,0,0,0.45)] sm:text-2xl"
        >
          {pillar.title}
        </h3>
        <p className="mt-2 max-w-[18rem] text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80 sm:text-xs">
          {pillar.subtitle}
        </p>
        <span
          className="mt-5 block h-px w-14 bg-gradient-to-r from-transparent via-white/50 to-transparent"
          aria-hidden
        />
        <p className="mt-5 max-w-[22rem] text-sm leading-relaxed text-white/90 [text-shadow:0_1px_12px_rgba(0,0,0,0.35)] sm:text-[0.9375rem]">
          {pillar.text}
        </p>
      </div>
    </article>
  )
}
